/**
 * "Errors" bucket card for the Action required group (sibling of the Manual Actions bucket).
 * The card row shows an error icon, label, total count (open RMAs with unresolved errors) and a chevron.
 * Clicking it opens a floating popup of error labels; picking labels adds removable chips to the card.
 * Error selection is mutually exclusive with the other filters and applies immediately on change.
 *
 * Unlike Manual Actions, the sub-row labels are the raw aggregated error message (no translation map),
 * and the bucket is NOT gated by the automation-tracking beta — it renders whenever the API returns errors.
 */
import { observer } from "mobx-react";
import "./rma-list-errors-bucket.scss";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import RmaListFiltersStore from "../../../rma-list-page/stores/rma-list-filters-store";
import RmaListErrorsPopup from "../rma-list-errors-popup/rma-list-errors-popup";
import RmaListErrorsBucketCardRow from "./rma-list-errors-bucket-card-row/rma-list-errors-bucket-card-row";
import RmaListErrorsBucketChips from "./rma-list-errors-bucket-chips/rma-list-errors-bucket-chips";
import useBucketPopup from "../../hooks/use-bucket-popup";

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled: boolean;
    onApply: () => void;
}

const RmaListErrorsBucket: React.FC<IProps> = observer(({ rmaListFiltersStore, disabled, onApply }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useBucketPopup(disabled);

    const selected = rmaListFiltersStore.selectedErrorLabels;
    const hasSelection = selected.length > 0;

    const getCount = (value: string) =>
        rmaListFiltersStore.errorOptions.find((option) => option.value === value)?.count ?? 0;

    const toggleLabel = (value: string) => {
        rmaListFiltersStore.toggleErrorLabel(value);
        onApply();
    };

    const toggleAllLabels = () => {
        rmaListFiltersStore.toggleAllErrors();
        onApply();
    };

    const cardActive = isOpen || hasSelection;
    // Empty bucket (no open RMAs with errors) stays visible but dimmed, unless the merchant is
    // actively interacting with it (open / has a selection).
    const isEmpty = rmaListFiltersStore.errorsTotal === 0 && !cardActive;

    return (
        <div className="rma-list-errors-bucket">
            <div className={`rma-list-errors-bucket__card${cardActive ? " active" : ""}${isEmpty ? " empty" : ""}${disabled ? " disabled" : ""}`}>
                <RmaListErrorsBucketCardRow
                    label={getTranslation("ERRORS", scope)}
                    total={rmaListFiltersStore.errorsTotal}
                    open={isOpen}
                    disabled={disabled}
                    triggerRef={refs.setReference}
                    triggerProps={getReferenceProps()}
                />

                {hasSelection && (
                    <RmaListErrorsBucketChips
                        selected={selected}
                        getCount={getCount}
                        disabled={disabled}
                        onRemove={toggleLabel}
                    />
                )}
            </div>

            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="rma-list-errors-bucket__popup"
                    {...getFloatingProps()}
                >
                    <RmaListErrorsPopup
                        rmaListFiltersStore={rmaListFiltersStore}
                        onToggle={toggleLabel}
                        onToggleAll={toggleAllLabels}
                    />
                </div>
            )}
        </div>
    );
});

export default RmaListErrorsBucket;
